/**
 * React hook for managing offline quote sync
 * Handles automatic sync on connection, manual sync triggers, and sync status
 */

import { useCallback, useEffect, useState } from "react";
import { getQueueStats } from "@/lib/sync/queue";
import {
  getIsSyncing,
  initializeSyncManager,
  onSyncComplete,
  retryFailedQuotes,
  type SyncResult,
  syncPendingQuotes,
  triggerManualSync,
} from "@/lib/sync/sync-manager";
import { useOnlineStatus } from "./useOnlineStatus";

export interface SyncStatus {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncResult: SyncResult | null;
  lastSyncTime: Date | null;
}

export function useOfflineSync() {
  const isOnline = useOnlineStatus();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isSyncing: false,
    pendingCount: 0,
    lastSyncResult: null,
    lastSyncTime: null,
  });

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    const stats = await getQueueStats();
    setSyncStatus((prev) => ({
      ...prev,
      pendingCount: stats.total,
    }));
  }, []);

  // Manual sync trigger
  const manualSync = useCallback(async () => {
    if (!isOnline) {
      console.log("[useOfflineSync] Cannot sync while offline");
      return;
    }

    setSyncStatus((prev) => ({ ...prev, isSyncing: true }));

    try {
      const result = await triggerManualSync();
      setSyncStatus((prev) => ({
        ...prev,
        isSyncing: false,
        lastSyncResult: result,
        lastSyncTime: new Date(),
      }));
      await updatePendingCount();
      return result;
    } catch (error) {
      console.error("[useOfflineSync] Manual sync error:", error);
      setSyncStatus((prev) => ({ ...prev, isSyncing: false }));
      throw error;
    }
  }, [isOnline, updatePendingCount]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && syncStatus.pendingCount > 0 && !getIsSyncing()) {
      console.log("[useOfflineSync] Device online, auto-syncing...");
      syncPendingQuotes().then(updatePendingCount);
    }
  }, [isOnline, syncStatus.pendingCount, updatePendingCount]);

  // Listen for sync results
  useEffect(() => {
    const unsubscribe = onSyncComplete((result) => {
      console.log("[useOfflineSync] Sync complete:", result);
      setSyncStatus((prev) => ({
        ...prev,
        isSyncing: false,
        lastSyncResult: result,
        lastSyncTime: new Date(),
      }));
      updatePendingCount();
    });

    return unsubscribe;
  }, [updatePendingCount]);

  // Listen for background sync messages from service worker
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "BACKGROUND_SYNC" && event.data?.action === "SYNC_QUOTES") {
        console.log("[useOfflineSync] Background sync triggered by service worker");
        syncPendingQuotes().then(updatePendingCount);
      }
    };

    navigator.serviceWorker?.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleMessage);
    };
  }, [updatePendingCount]);

  // Initialize sync manager
  useEffect(() => {
    initializeSyncManager();
    updatePendingCount();
  }, [updatePendingCount]);

  // Periodic retry of failed quotes
  useEffect(() => {
    if (!isOnline) return;

    const retryInterval = setInterval(() => {
      retryFailedQuotes().then(updatePendingCount);
    }, 60000); // Retry every minute

    return () => clearInterval(retryInterval);
  }, [isOnline, updatePendingCount]);

  return {
    syncStatus,
    manualSync,
    isOnline,
  };
}
